import { useEffect, useMemo, useState } from "react";

import api from "../api/axios";
import { NOTE_STYLES } from "../components/canvas/constants";

/**
 * Owns the team board's notes plus the search/tag filter.
 *
 * Lifted out of the Notes page so the corkboard and the studio's note
 * library render from one source of truth — editing a note in either place
 * updates both, and any canvas it has been dropped onto.
 */
export default function useNotes({ isAdmin }) {
  const [notes, setNotes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [query, setQuery] = useState("");
  const [tagFilter, setTagFilter] = useState(null);

  useEffect(() => {
    let active = true;
    api
      .get("/notes")
      .then(({ data }) => active && setNotes(data))
      .catch(() => active && setError("Could not load notes. Is the server running?"))
      .finally(() => active && setLoading(false));
    return () => {
      active = false;
    };
  }, [isAdmin]);

  const addNote = async () => {
    const n = notes.length;
    const stagger = 24 + (n % 6) * 26;
    try {
      const { data } = await api.post("/notes", {
        text: "",
        color: n % NOTE_STYLES.length,
        x: stagger,
        y: stagger,
      });
      setNotes((prev) => [...prev, data]);
    } catch {
      setError("Could not add note.");
    }
  };

  // Optimistic local edit — used for every keystroke and drag frame.
  const patchLocal = (id, changes) =>
    setNotes((prev) => prev.map((nn) => (nn._id === id ? { ...nn, ...changes } : nn)));

  const patchNote = async (id, changes) => {
    patchLocal(id, changes);
    try {
      await api.put(`/notes/${id}`, changes);
    } catch {
      setError("Could not save changes.");
    }
  };

  const deleteNote = async (id) => {
    const prev = notes;
    setNotes((p) => p.filter((n) => n._id !== id));
    try {
      await api.delete(`/notes/${id}`);
    } catch {
      setError("Could not delete note.");
      setNotes(prev);
    }
  };

  const addTag = (note, value) => {
    const tag = value.trim();
    if (!tag || note.tags?.includes(tag)) return;
    patchNote(note._id, { tags: [...(note.tags || []), tag] });
  };

  const removeTag = (note, tag) =>
    patchNote(note._id, { tags: (note.tags || []).filter((t) => t !== tag) });

  const visible = useMemo(() => {
    const q = query.trim().toLowerCase();
    return notes.filter((n) => {
      if (tagFilter && !(n.tags || []).includes(tagFilter)) return false;
      if (!q) return true;
      return (
        (n.text || "").toLowerCase().includes(q) ||
        (n.tags || []).some((t) => t.toLowerCase().includes(q)) ||
        (n.ownerName || "").toLowerCase().includes(q)
      );
    });
  }, [notes, query, tagFilter]);

  const allTags = useMemo(
    () => [...new Set(notes.flatMap((n) => n.tags || []))],
    [notes]
  );

  return {
    notes,
    visible,
    allTags,
    loading,
    error,
    setError,
    query,
    setQuery,
    tagFilter,
    setTagFilter,
    addNote,
    patchLocal,
    patchNote,
    deleteNote,
    addTag,
    removeTag,
  };
}
